'use client'

import { createContext, useContext, useState } from 'react'
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline'

const DownloadContext = createContext()

export function DownloadProvider({ children }) {
  const [downloads, setDownloads] = useState([])

  const startDownload = (fileName) => {
    const id = Date.now()
    setDownloads(prev => [...prev, { id, fileName, progress: 0 }])
    return id
  }

  const updateProgress = (id, progress) => {
    setDownloads(prev => 
      prev.map(d => d.id === id ? { ...d, progress } : d)
    )
  }

  const completeDownload = (id) => {
    // Keep for 2 seconds then remove
    setTimeout(() => {
      setDownloads(prev => prev.filter(d => d.id !== id))
    }, 2000)
  }

  const removeDownload = (id) => {
    setDownloads(prev => prev.filter(d => d.id !== id))
  }

  return (
    <DownloadContext.Provider value={{ startDownload, updateProgress, completeDownload, removeDownload }}>
      {children}
      
      {/* Global Download Notifications */}
      <div className="fixed bottom-4 right-4 z-50 space-y-2">
        {downloads.map((download) => (
          <div 
            key={download.id}
            className="max-w-sm w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4"
          >
            <div className="flex items-start gap-3">
              <ArrowDownTrayIcon className="h-5 w-5 text-sky-600 dark:text-sky-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Downloading
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                  {download.fileName}
                </p>
                <div className="mt-2">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                    <div 
                      className="bg-sky-600 dark:bg-sky-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${download.progress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {download.progress > 0 ? `${download.progress}%` : 'Starting...'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </DownloadContext.Provider>
  )
}

export function useDownload() {
  const context = useContext(DownloadContext)
  if (!context) {
    throw new Error('useDownload must be used within DownloadProvider')
  }
  return context
}
